// Static, code-keyed billing knowledge for the ordering engine: V28 risk
// relevance, MS-DRG MCC/CC severity, etiology/manifestation ("code first")
// pairs, and LCD wound-supply qualification. Every table here is a curated
// STARTER SUBSET of well-established, publicly documented mappings, not the
// full CMS lists - enough to make the profiles behave correctly on common
// primary-care / wound-care / inpatient codes. A backend-scored flag on the
// item (item.v28, item.mcc, item.cc, item.lcdCovered) always wins over these
// tables, so server-side enrichment can land later with no engine change.
// Like factors.js, readers tolerate missing fields and never throw mid-sort.

const codeOf = (item) => {
  if (item == null) return "";
  if (typeof item === "string") return item;
  return String(item.code || "").toUpperCase();
};

const descOf = (item) => (item && typeof item.description === "string" ? item.description : "");

// --- Specificity -------------------------------------------------------------

// "Unspecified" in the official description (M54.50, E66.9) means the code is
// NOT the fully-specified choice even though it carries a decimal extension,
// so the decimal heuristic alone over-credits it.
export function isUnspecifiedDescription(item) {
  return /\bunspecified\b/i.test(descOf(item));
}

// --- Acute vs chronic (guideline I.B.8: acute sequences first) ----------------

export function isAcute(item) {
  if (item && item.acute === true) return true;
  return /\b(?:sub)?acute\b/i.test(descOf(item));
}

// --- V28 / HCC risk relevance --------------------------------------------------

// Prefix -> relative ordering tier (3 high, 1 low). Tiers are deliberately
// coarse ordering weights, NOT CMS RAF coefficients - they exist so a heart
// failure outranks uncomplicated diabetes until the backend supplies real
// per-code values via item.hccValue.
const V28_TIERS = [
  { test: /^I50/, tier: 3 }, // heart failure
  { test: /^J96/, tier: 3 }, // respiratory failure
  { test: /^N18\.[56]/, tier: 3 }, // CKD stage 5 / ESRD
  { test: /^C(18|19|20|34|50)/, tier: 3 }, // major malignancies
  { test: /^N18\.4/, tier: 2 }, // CKD stage 4
  { test: /^E(08|09|10|11|13)\.(?!9)/, tier: 2 }, // diabetes with complications
  { test: /^J44/, tier: 2 }, // COPD
  { test: /^I48/, tier: 2 }, // atrial fibrillation
  { test: /^M0[56]/, tier: 2 }, // rheumatoid arthritis
  { test: /^F33\.[123]/, tier: 2 }, // recurrent major depression
  { test: /^(G30|F0[123])/, tier: 2 }, // Alzheimer's / dementias
  { test: /^E66\.(01|2)/, tier: 2 }, // morbid obesity / hypoventilation
  { test: /^E(08|09|10|11|13)\.9/, tier: 1 }, // diabetes without complications
];

export function v28Tier(item) {
  const code = codeOf(item);
  if (!code) return 0;
  const hit = V28_TIERS.find((r) => r.test.test(code));
  return hit ? hit.tier : 0;
}

export function isV28Static(item) {
  return v28Tier(item) > 0;
}

// --- MS-DRG severity (MCC = 2, CC = 1) ----------------------------------------

const MCC_PATTERNS = [
  /^A4[01]/, // sepsis
  /^R65\.2/, // severe sepsis
  /^J96\.[02]/, // acute (and acute-on-chronic) respiratory failure
  /^I21/, // acute myocardial infarction
  /^I50\.(21|23|31|33|41|43)/, // acute (on chronic) systolic/diastolic heart failure
  /^N17\.0/, // acute kidney failure with tubular necrosis
  /^G93\.41/, // metabolic encephalopathy
  /^L89\.\d+[34]$/, // pressure ulcer stage 3-4
];

const CC_PATTERNS = [
  /^N17\.[89]/, // acute kidney failure, other/unspecified
  /^D62/, // acute posthemorrhagic anemia
  /^J18/, // pneumonia
  /^J44\.[01]/, // COPD with exacerbation/infection
  /^I50\.(22|32|42)/, // chronic systolic/diastolic heart failure
  /^E87\.[12]/, // hyponatremia, acidosis
  /^E86\.0/, // dehydration
  /^K92\.2/, // GI hemorrhage
  /^N39\.0/, // UTI
  /^F05/, // delirium
  /^L97/, // non-pressure chronic ulcers
  /^L89\.\d+2$/, // pressure ulcer stage 2
];

export function drgSeverity(item) {
  if (item && item.mcc === true) return 2;
  if (item && item.cc === true) return 1;
  const code = codeOf(item);
  if (!code) return 0;
  if (MCC_PATTERNS.some((p) => p.test(code))) return 2;
  if (CC_PATTERNS.some((p) => p.test(code))) return 1;
  return 0;
}

// --- Etiology / manifestation ("code first") pairs ------------------------------

// Manifestation prefix -> etiology prefixes that must sequence before it when
// both appear on the same note. Mandatory ICD-10-CM convention, not a style
// preference, so the engine applies it as an adjacency pass after sorting.
const MANIFESTATION_PAIRS = [
  { manifestation: /^L97/, etiologies: ["E08", "E09", "E10", "E11", "E13", "I70"] }, // diabetic/arterial ulcers
  { manifestation: /^N18/, etiologies: ["I12", "I13"] }, // hypertensive CKD
  { manifestation: /^D63\.1/, etiologies: ["N18"] }, // anemia in CKD
  { manifestation: /^D63\.0/, etiologies: ["C"] }, // anemia in neoplastic disease
  { manifestation: /^F02/, etiologies: ["G30", "G31"] }, // dementia in other diseases
];

// Returns the etiology prefixes for a manifestation code, or null.
export function etiologyPrefixesFor(item) {
  const code = codeOf(item);
  if (!code) return null;
  const hit = MANIFESTATION_PAIRS.find((p) => p.manifestation.test(code));
  return hit ? hit.etiologies : null;
}

export function isEtiologyFor(item, etiologyPrefixes) {
  const code = codeOf(item);
  return !!code && etiologyPrefixes.some((p) => code.startsWith(p));
}

// --- LCD supply qualification (surgical dressings / wound care) -----------------

// Diagnoses that establish medical necessity for wound-care supplies under the
// surgical-dressings LCD family: pressure ulcers, non-pressure chronic ulcers,
// non-healing surgical wounds, dehiscence.
const LCD_WOUND_PATTERNS = [/^L89/, /^L97/, /^L98\.4/, /^T81\.3/];

export function isLcdQualifying(item) {
  if (item && item.lcdCovered === true) return true;
  const code = codeOf(item);
  return !!code && LCD_WOUND_PATTERNS.some((p) => p.test(code));
}
